import React, { useCallback, useMemo } from "react";
import { useDropzone, Accept, FileRejection } from "react-dropzone";
import styled from "styled-components";
import toast from "react-hot-toast";

import { getFileIconFromFile } from "../file-icon/fileIcon";
import { formatDropRejection, withExtensionFallback } from "../../utils/fileType";

// ===== Styled Components =====

const Container = styled.div`
  width: 100%;
`;

const FileList = styled.div`
  margin-bottom: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background-color: var(--soremo-background);
  border-radius: 4px;
`;

const FileIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 16px;
  color: var(--soremo-light-gray);
  flex-shrink: 0;
`;

const FileName = styled.span.attrs({
  className: "bodytext-11",
})`
  color: var(--soremo-gray);
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--soremo-light-gray);
  transition: color 0.2s;

  &:hover {
    color: var(--soremo-blue);
  }
`;

const RemoveIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 16px;
`;

interface DropzoneProps {
  $isDragActive: boolean;
  $hasError: boolean;
  $height: string | number;
}

const Dropzone = styled.div.attrs({ className: "bodytext-13" })<DropzoneProps>`
  width: 100%;
  height: ${props => typeof props.$height === 'number' ? `${props.$height}px` : props.$height};
  border: 2px dashed ${props =>
    props.$hasError ? 'var(--soremo-green)' :
    props.$isDragActive ? 'var(--soremo-blue)' : 'var(--soremo-border)'};
  border-radius: 6px;
  padding: 24px 16px;
  text-align: center;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => 
    props.$isDragActive ? 'rgba(0, 154, 206, 0.05)' : 
    props.$hasError ? 'rgba(44, 200, 77, 0.05)' : 'var(--soremo-background)'};
  transition: all 0.3s ease;
  color: var(--soremo-light-gray);
  
  .material-symbols-rounded {
    color: ${props =>
      props.$isDragActive ? 'var(--soremo-blue)' :
      props.$hasError ? 'var(--soremo-green)' : 'var(--soremo-border)'};
    transition: color 0.3s ease;
  }

  &:hover {
    background-color: rgba(0, 154, 206, 0.05);
    border-color: var(--soremo-blue);
    color: var(--soremo-blue);

    .material-symbols-rounded {
      color: var(--soremo-blue);
    }
  }
`;

const UploadIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 48px;
  display: block;
`;

const ErrorMessage = styled.p.attrs({
  className: "bodytext-11",
})`
  color: var(--soremo-green);
  margin-top: 4px;
`;

// ===== Types =====

export interface FileUploaderProps {
  /** 選択されたファイル */
  files: File[];
  /** ファイル変更時のコールバック */
  onChange: (files: File[]) => void;
  /** 複数ファイル許可 */
  multiple?: boolean;
  /** 許可するファイルタイプ（MIMEタイプまたはAccept形式） */
  accept?: Accept | string;
  /** 最大ファイルサイズ（bytes） */
  maxSize?: number;
  /** 最大ファイル数 */
  maxFiles?: number;
  /** ドロップゾーンの高さ */
  height?: number | string;
  /** プレースホルダーテキスト */
  placeholder?: string;
  /** ドラッグ中のテキスト */
  dragActiveText?: string;
  /** API連携用コールバック（オプション） */
  onUpload?: (file: File) => Promise<void>;
  /** 無効状態 */
  disabled?: boolean;
  /** エラーメッセージ */
  error?: string;
}

// ===== Helper Functions =====

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const parseAccept = (accept: Accept | string | undefined): Accept | undefined => {
  if (!accept) return undefined;
  if (typeof accept === 'string') {
    // シンプルな文字列の場合（例: "image/*" や ".pdf,.doc"）
    const mimeTypes = accept.split(',').map(s => s.trim());
    const result: Accept = {};
    mimeTypes.forEach(type => {
      if (type.startsWith('.')) {
        // 拡張子の場合
        result['application/octet-stream'] = result['application/octet-stream'] || [];
        result['application/octet-stream'].push(type);
      } else {
        result[type] = [];
      }
    });
    return result;
  }
  return accept;
};

// ===== Component =====

const FileUploader: React.FC<FileUploaderProps> = ({
  files,
  onChange,
  multiple = false,
  accept,
  maxSize,
  maxFiles,
  height = 160,
  placeholder = "クリックまたはドラッグ＆ドロップ",
  dragActiveText = "ファイルをここにドロップ",
  onUpload,
  disabled = false,
  error,
}) => {
  // MIME が空になる端末（Windows の一部）でも弾かれないよう拡張子を補う
  const parsedAccept = useMemo(() => {
    const parsed = parseAccept(accept);
    return parsed ? withExtensionFallback(parsed) : undefined;
  }, [accept]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (disabled) return;

      // 弾かれたファイルが黙って消えると、選択した本人が漏れに気付けない
      rejectedFiles.forEach((rejection) => {
        toast.error(formatDropRejection(rejection));
      });

      // maxFilesを超えないように制限
      let newFiles = acceptedFiles;
      if (maxFiles) {
        const remainingSlots = maxFiles - files.length;
        newFiles = acceptedFiles.slice(0, remainingSlots);
      }

      // multipleがfalseの場合は1ファイルのみ
      if (!multiple) {
        newFiles = newFiles.slice(0, 1);
        onChange(newFiles);
      } else {
        onChange([...files, ...newFiles]);
      }

      // API連携が設定されている場合は自動アップロード
      if (onUpload) {
        for (const file of newFiles) {
          try {
            await onUpload(file);
          } catch (err) {
            console.error(`Failed to upload ${file.name}:`, err);
          }
        }
      }
    },
    [disabled, files, maxFiles, multiple, onChange, onUpload]
  );

  const removeFile = useCallback(
    (index: number) => {
      const newFiles = files.filter((_, i) => i !== index);
      onChange(newFiles);
    },
    [files, onChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: parsedAccept,
    maxSize,
    maxFiles: multiple ? maxFiles : 1,
    multiple,
    disabled,
  });

  return (
    <Container>
      {files.length > 0 && (
        <FileList>
          {files.map((file, index) => (
            <FileItem key={`${file.name}-${index}`}>
              <FileIcon>{getFileIconFromFile(file)}</FileIcon>
              <FileName>
                {file.name} ({formatFileSize(file.size)})
              </FileName>
              <RemoveButton
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
                aria-label={`${file.name}を削除`}
              >
                <RemoveIcon>close</RemoveIcon>
              </RemoveButton>
            </FileItem>
          ))}
        </FileList>
      )}

      <Dropzone
        {...getRootProps()}
        $isDragActive={isDragActive}
        $hasError={!!error}
        $height={height}
      >
        <input {...getInputProps()} />
        <div className="u-col-center">
          <UploadIcon>
            {isDragActive ? 'file_download' : 'file_upload'}
          </UploadIcon>
          {isDragActive ? dragActiveText : placeholder}
          {maxSize && (
            <span className="bodytext-11 u-mt4" style={{ color: 'var(--soremo-light-gray)' }}>
              最大サイズ: {formatFileSize(maxSize)}
            </span>
          )}
        </div>
      </Dropzone>

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Container>
  );
};

export default FileUploader;

